import { useState } from "react"
import { addWeeks } from "date-fns"

import { Button } from "~/components/reusable/Button"
import { Label } from "~/components/reusable/Label"
import DatePicker from "~/components/reusable/DatePicker"
import {
  DialogRoot,
  DialogContentV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogFooterV2,
  DialogTitle,
  DialogClose,
} from "~/components/reusable/Dialog"
import { PermissionTooltip } from "~/components/design-system/components"
import { useFlagsActionDialogStore } from "~/stores/flags-action-dialog-store"
import { useMutateActivateSkus, useMutateDeactivateSkus } from "~/hooks/useManageSkus"
import { usePermission } from "~/hooks/usePermission"
import { getStartOfCurrentWeekUTC, isMondayDatePicker } from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"
import { formatVariantId } from "~/utils/helpers"
import { Card } from "~/components/reusable/Card"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

function FlagsActionDialogContent({ variants }: { variants: IVariantSummary[] }) {
  const [date, setDate] = useState<Date>(() => addWeeks(getStartOfCurrentWeekUTC(), 1))
  const deactivateSkus = useMutateDeactivateSkus()
  const activateSkus = useMutateActivateSkus()
  const canEdit = usePermission("edit")

  return (
    <DialogContentV2>
      <DialogHeaderV2>
        <DialogTitle>Update {variants.length} SKUs</DialogTitle>
      </DialogHeaderV2>
      <DialogBodyV2>
        <div className="flex flex-col gap-2 text-sm h-max-[200px] w-fit">
          {variants.map((v) => (
            <Card key={v.variantId} className="flex flex-col p-2">
              <span>{v.variantName}</span>
              <span className="text-fg-muted">{formatVariantId(v.variantId)}</span>
            </Card>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          <Label>Set expected first {formatName("sale")} date to</Label>
          <DatePicker
            value={date}
            filterDate={isMondayDatePicker}
            onChange={(day) => day && setDate(day)}
            minDate={addWeeks(getStartOfCurrentWeekUTC(), 1)}
          />
        </div>
      </DialogBodyV2>
      {/* `DialogClose` is not one of the gated triggers, so these use the wrapper escape hatch. */}
      <DialogFooterV2 className="justify-end gap-4">
        <PermissionTooltip requires="edit">
          <DialogClose asChild>
            <Button
              disabled={deactivateSkus.isPending || !canEdit.allowed}
              variant="danger"
              onClick={() =>
                deactivateSkus.mutate({
                  variantIds: variants.map((v) => v.variantId),
                  removeFromRelevantCollections: true,
                })
              }
            >
              Deactivate {variants.length} SKUs
            </Button>
          </DialogClose>
        </PermissionTooltip>
        <PermissionTooltip requires="edit">
          <DialogClose asChild>
            <Button
              disabled={activateSkus.isPending || !canEdit.allowed}
              onClick={() =>
                activateSkus.mutate({
                  activatedFrom: date,
                  variantIds: variants.map((v) => v.variantId),
                  reactivate: true,
                })
              }
            >
              Update expected start date
            </Button>
          </DialogClose>
        </PermissionTooltip>
      </DialogFooterV2>
    </DialogContentV2>
  )
}

export function FlagsActionDialog() {
  const { open, variants, closeDialog } = useFlagsActionDialogStore()

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) closeDialog()
      }}
    >
      <FlagsActionDialogContent key={String(open)} variants={variants} />
    </DialogRoot>
  )
}
